/**
 * Textbausteine pflegen.
 *
 * Die Reihenfolge kommt vom Server, sortiert nach Häufigkeit. Deshalb steht
 * hier die Zahl der Verwendungen dabei – so ist nachvollziehbar, warum ein
 * Baustein oben steht.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useBausteinAendern,
  useBausteinAnlegen,
  useBausteinLoeschen,
  useBausteine,
  useGeraetetypen,
} from '../api/abfragen'
import { STUFEN, type Bausteinkategorie, type Stufe, type Textbaustein } from '../api/typen'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, Stufenzeichen } from '../bausteine/Hinweise'
import { Kundenarten } from '../bausteine/Kundenarten'
import { darfSchreiben, darfVerwalten, useKonto } from '../konto/KontoKontext'

const KATEGORIEN: { wert: Bausteinkategorie; text: string }[] = [
  { wert: 'mangel', text: 'Mängel' },
  { wert: 'bemerkung', text: 'Bemerkungen' },
  { wert: 'normverweis', text: 'Normverweise' },
]

export function Bausteine() {
  const { konto } = useKonto()
  const [kategorie, setzeKategorie] = useState<Bausteinkategorie>('mangel')
  const bausteine = useBausteine(kategorie)
  const anlegen = useBausteinAnlegen()
  const typen = useGeraetetypen()
  const [nurTyp, setzeNurTyp] = useState('')
  const [formularOffen, setzeFormularOffen] = useState(false)

  return (
    <div className="space-y-4">
      <p className="text-base text-slate-700">
        Prüfabläufe – die Schrittfolgen je Gerätetyp – stehen unter{' '}
        <Link to="/pruefablaeufe" className="text-marke-700 underline">
          Prüfabläufe
        </Link>
        . Die Punkte der Platzbeurteilung nach DIN 18034 – Einfriedung,
        angrenzende Bäume und Gebäude, Gewässer – unter{' '}
        <Link to="/pruefpunkte" className="text-marke-700 underline">
          Prüfpunkte Spielplatz
        </Link>
        .
      </p>
      <Kundenarten />

      <hr className="border-t-2 border-slate-200" />

      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Textbausteine</h1>
        {darfSchreiben(konto) && (
          <button
            type="button"
            className="knopf-haupt ml-auto"
            onClick={() => setzeFormularOffen((o) => !o)}
          >
            {formularOffen ? 'Abbrechen' : 'Neuer Baustein'}
          </button>
        )}
      </header>

      <p className="text-base text-slate-700">
        Im Prüfbogen erscheinen die Bausteine nach Häufigkeit sortiert – was oft gebraucht
        wird, steht nach kurzer Zeit oben.
      </p>

      <div className="flex gap-2">
        {KATEGORIEN.map((k) => (
          <button
            key={k.wert}
            type="button"
            onClick={() => setzeKategorie(k.wert)}
            aria-pressed={kategorie === k.wert}
            className={kategorie === k.wert ? 'knopf-haupt flex-1' : 'knopf-still flex-1'}
          >
            {k.text}
          </button>
        ))}
      </div>

      {formularOffen && (
        <form
          className="karte space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            const f = new FormData(e.currentTarget)
            const stufe = String(f.get('stufe_vorschlag') ?? '')
            anlegen.mutate(
              {
                kategorie,
                text: String(f.get('text')),
                stufe_vorschlag: stufe ? (stufe as Stufe) : null,
                normverweis: String(f.get('normverweis') ?? '') || null,
                geraetetyp: String(f.get('geraetetyp') ?? '') || null,
              },
              {
                onSuccess: () => {
                  setzeFormularOffen(false)
                  anlegen.reset()
                },
              },
            )
          }}
        >
          <div>
            <label htmlFor="text">Text</label>
            <textarea id="text" name="text" required style={{ minHeight: '72px' }} />
          </div>

          {/* Zuordnung zum Gerätetyp. Ohne sie wird jeder Baustein überall
              vorgeschlagen — bei fünfzig Bausteinen findet man den
              richtigen dann nicht mehr. „Kette verschlissen“ hat am
              Sandkasten nichts zu suchen. */}
          <div>
            <label htmlFor="geraetetyp">Gilt für</label>
            <select id="geraetetyp" name="geraetetyp" defaultValue="">
              <option value="">Alle Geräte und Platzkriterien</option>
              {(typen.data ?? []).map((typ) => (
                <option key={typ.id} value={typ.name}>
                  Nur {typ.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-base text-slate-600">
              Ein zugeordneter Baustein erscheint im Prüfbogen nur bei diesem
              Gerätetyp — und dort ganz oben.
            </p>
          </div>
          {kategorie === 'mangel' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="stufe_vorschlag">Stufenvorschlag</label>
                <select id="stufe_vorschlag" name="stufe_vorschlag" defaultValue="">
                  <option value="">— keiner —</option>
                  {STUFEN.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="normverweis">Normverweis</label>
                <input id="normverweis" name="normverweis" placeholder="DIN EN 1176-2" />
              </div>
            </div>
          )}

          <Fehlerhinweis fehler={anlegen.error} />

          <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Baustein anlegen'}
          </button>
        </form>
      )}

      {/* Filter nach Gerätetyp. Die Liste wird lang, und wer einen Text
          für die Schaukel sucht, will nicht durch die Rutschen scrollen. */}
      {(bausteine.data ?? []).some((b) => b.geraetetyp) && (
        <div>
          <label htmlFor="filter-typ">Anzeigen</label>
          <select
            id="filter-typ"
            value={nurTyp}
            onChange={(e) => setzeNurTyp(e.target.value)}
          >
            <option value="">Alle Bausteine</option>
            <option value="__allgemein">Nur allgemeine (ohne Gerätetyp)</option>
            {[...new Set((bausteine.data ?? []).map((b) => b.geraetetyp).filter(Boolean))]
              .sort()
              .map((typ) => (
                <option key={typ as string} value={typ as string}>
                  {typ}
                </option>
              ))}
          </select>
        </div>
      )}

      {bausteine.isLoading ? (
        <Ladeanzeige />
      ) : (bausteine.data ?? []).length === 0 ? (
        <Leerhinweis titel="Keine Bausteine in dieser Kategorie" />
      ) : (
        <ul className="space-y-2">
          {(bausteine.data ?? [])
            .filter((b) =>
              nurTyp === ''
                ? true
                : nurTyp === '__allgemein'
                  ? !b.geraetetyp
                  : b.geraetetyp === nurTyp,
            )
            .map((b) => (
              <Bausteinzeile key={b.id} baustein={b} darf={darfVerwalten(konto)} />
            ))}
        </ul>
      )}
    </div>
  )
}


/**
 * Eine Zeile der Bausteinliste, mit Bearbeitung.
 *
 * Bausteine wachsen mit dem Gebrauch: aus „Schraube locker“ wird nach
 * einem halben Jahr „Schraubverbindung locker, nachziehen“. Bisher blieb
 * nur, einen zweiten anzulegen und den alten stehen zu lassen — der
 * Katalog wurde damit länger statt besser.
 *
 * Die Kategorie lässt sich nicht ändern: ein Mangeltext, der zur Bemerkung
 * wird, erschiene an ganz anderer Stelle in den Vorschlägen, und die
 * bisherigen Verwendungen zählten für etwas anderes.
 */
function Bausteinzeile({ baustein, darf }: { baustein: Textbaustein; darf: boolean }) {
  const aendern = useBausteinAendern()
  const loeschen = useBausteinLoeschen()
  const [offen, setzeOffen] = useState(false)
  const [text, setzeText] = useState(baustein.text)
  const [stufe, setzeStufe] = useState<Stufe | ''>(baustein.stufe_vorschlag ?? '')
  const [norm, setzeNorm] = useState(baustein.normverweis ?? '')
  const [typ, setzeTyp] = useState(baustein.geraetetyp ?? '')
  const typen = useGeraetetypen()

  function speichern(ereignis: React.FormEvent) {
    ereignis.preventDefault()
    aendern.mutate(
      {
        id: baustein.id,
        text: text.trim(),
        stufe_vorschlag: (stufe || null) as Stufe | null,
        normverweis: norm.trim() || null,
        geraetetyp: typ || null,
      },
      { onSuccess: () => setzeOffen(false) },
    )
  }

  if (!offen) {
    return (
      <li
        className={`karte flex items-center gap-3 ${
          !baustein.aktiv ? 'bg-slate-50' : ''
        }`}
      >
        {baustein.stufe_vorschlag && (
          <Stufenzeichen stufe={baustein.stufe_vorschlag} klein />
        )}
        <div className="min-w-0 flex-1">
          <div className={!baustein.aktiv ? 'text-slate-500 line-through' : ''}>
            {baustein.text}
          </div>
          <div className="text-base text-slate-600">
            {baustein.geraetetyp ? (
              <span className="marke bg-akzent-grund text-akzent-700">
                {baustein.geraetetyp}
              </span>
            ) : null}
            {baustein.normverweis && (
              <span className={baustein.geraetetyp ? 'ml-2' : ''}>
                {baustein.normverweis}
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-sm text-slate-600">{baustein.verwendungen}×</div>
        {darf && (
          <button
            type="button"
            className="knopf-still px-3 text-base"
            onClick={() => setzeOffen(true)}
          >
            Ändern
          </button>
        )}
      </li>
    )
  }

  return (
    <li className="karte">
      <form className="space-y-3" onSubmit={speichern}>
        <div>
          <label htmlFor={`t-${baustein.id}`}>Text</label>
          <textarea
            id={`t-${baustein.id}`}
            value={text}
            onChange={(e) => setzeText(e.target.value)}
            required
            rows={2}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`s-${baustein.id}`}>Stufenvorschlag</label>
            <select
              id={`s-${baustein.id}`}
              value={stufe}
              onChange={(e) => setzeStufe(e.target.value as Stufe | '')}
            >
              <option value="">(keiner)</option>
              {STUFEN.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`n-${baustein.id}`}>Normverweis</label>
            <input
              id={`n-${baustein.id}`}
              value={norm}
              onChange={(e) => setzeNorm(e.target.value)}
              maxLength={60}
              placeholder="DIN EN 1176-2"
            />
          </div>
        </div>

        <div>
          <label htmlFor={`g-${baustein.id}`}>Gilt für</label>
          <select
            id={`g-${baustein.id}`}
            value={typ}
            onChange={(e) => setzeTyp(e.target.value)}
          >
            <option value="">Alle Geräte und Platzkriterien</option>
            {(typen.data ?? []).map((x) => (
              <option key={x.id} value={x.name}>
                Nur {x.name}
              </option>
            ))}
          </select>
        </div>

        <p className="text-base text-slate-600">
          {baustein.verwendungen}× verwendet. Änderungen wirken auf künftige
          Vorschläge — was in abgeschlossenen Berichten steht, bleibt dort
          unverändert.
        </p>

        <Fehlerhinweis fehler={aendern.error} />
        <Fehlerhinweis fehler={loeschen.error} />

        <div className="flex flex-wrap gap-2">
          <button type="submit" className="knopf-haupt" disabled={aendern.isPending}>
            {aendern.isPending ? 'Speichert …' : 'Übernehmen'}
          </button>
          <button
            type="button"
            className="knopf-still"
            onClick={() => {
              setzeText(baustein.text)
              setzeStufe(baustein.stufe_vorschlag ?? '')
              setzeNorm(baustein.normverweis ?? '')
              setzeTyp(baustein.geraetetyp ?? '')
              setzeOffen(false)
            }}
          >
            Abbrechen
          </button>
          {/* Zwei verschiedene Dinge, deshalb zwei Knöpfe.

              Stilllegen ist für den Baustein, den man vielleicht wieder
              braucht: er verschwindet aus dem Prüfbogen und bleibt hier.
              Löschen ist für die Zeile, die gar nicht erst hätte entstehen
              sollen. Beides ist unbedenklich für alte Berichte – der Text
              wird beim Verwenden in den Befund kopiert, nicht verlinkt. */}
          <button
            type="button"
            className="knopf-still ml-auto px-3"
            onClick={() =>
              aendern.mutate(
                { id: baustein.id, aktiv: !baustein.aktiv },
                { onSuccess: () => setzeOffen(false) },
              )
            }
          >
            {!baustein.aktiv ? 'Wieder verwenden' : 'Stilllegen'}
          </button>
          <button
            type="button"
            className="knopf-warnung px-3"
            disabled={loeschen.isPending}
            onClick={() => {
              const frage =
                `Textbaustein „${baustein.text}" endgültig löschen?` +
                (baustein.verwendungen > 0
                  ? `\n\nEr wurde ${baustein.verwendungen}× verwendet. In den ` +
                    'geschriebenen Berichten bleibt der Text stehen – dort ist er ' +
                    'kopiert, nicht verlinkt.'
                  : '')
              if (window.confirm(frage)) loeschen.mutate(baustein.id)
            }}
          >
            Löschen
          </button>
        </div>
      </form>
    </li>
  )
}
